









































































































































import {
  SfButton,
  SfImage,
  SfList,
  SfPrice,
  SfQuantitySelector,
  SfProductCard,
  SfHeading
} from '~/components';

import SizeSelector from '~/modules/catalog/product/components/product-types/grouped/SizeSelector.vue';

import {
  defineComponent,
  computed,
  watch,
  ref,
  PropType,
  useContext,
  useRouter,
  useRoute,
} from '@nuxtjs/composition-api';

import {
  useImage,
  useProduct,
  useUiNotification,
  useUser,
  useWishlist,
  useCart, ProductDetailsQuery
} from '~/composables';

import {
  getGroupedProducts,
  getName as getProductName,
  getPrice as getProductPrice,
  getProductThumbnailImage,
  getProductSmallImage,
  getTagLine,
} from '~/modules/catalog/product/getters/productGetters';

import { getGroupedProductPriceCommand } from '~/modules/catalog/pricing/getGroupedProductPriceCommand';

import type { WithTypename } from '~/modules/catalog/product/types';
import type { GroupedProduct } from '~/modules/GraphQL/types';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { ProductTypeEnum } from '~/modules/catalog/product/enums/ProductTypeEnum';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import loginForm from '~/modules/customer/components/LoginModal/forms/LoginForm.vue';

export default defineComponent({
  name: 'GroupedProductSelector',
  components: {
    SfList,
    SfPrice,
    SfButton,
    SfQuantitySelector,
    SfImage,
    SfProductCard,
    SfHeading,
    SizeSelector,
  },
  props: {
    canAddToCart: {
      type: Boolean,
      required: false,
      default: true,
    },
    product: {
      type: Object as PropType<WithTypename<GroupedProduct>>,
      required: true,
    },
  },
  emits: ['update-price'],
  setup(props, { emit }) {
    const { app } = useContext();
    const { isAuthenticated } = useUser();
    const router = useRouter();
    const route = useRoute();
    const { isInWishlist, addOrRemoveItem } = useWishlist();
    const { addItem, error: cartError } = useCart();
    const { addItemToCart, isInCart } = useAddToCart();
    const { send: sendNotification } = useUiNotification();
    const { getProductPath } = useProduct();
    const loading = ref(false);


    const groupedItems = computed(() => getGroupedProducts(props.product));
    const isSomeGroupedItemsSelect = computed(() => {
      return groupedItems.value.some(item => item.qty !== 0);
    });
    const isSimpleProduct = (groupedItem) => groupedItem.product.__typename === ProductTypeEnum.SIMPLE_PRODUCT;

    const productPrice = computed(() => getGroupedProductPriceCommand(props.product));

    const selectedSize = ref(null);

    const selectedProductsConfiguration = ref([]);
    const selectedProductsConfigurationReady = computed(() => {
      return selectedProductsConfiguration.value.length === groupedItems.value.filter(item => item.product.__typename !== ProductTypeEnum.SIMPLE_PRODUCT).length;
    });

    const differenceInLength = computed(() => {
      return groupedItems.value.length - selectedProductsConfiguration.value.length;
    });

    const handleSelectedSize = (size) => {
      const index = selectedProductsConfiguration.value.findIndex(item => item.uid === size.value.uid);

      if (index !== -1) {
        selectedProductsConfiguration.value[index] = size.value;
      } else {
        selectedProductsConfiguration.value.push(size.value);
      }
    };
    const addToCart = async () => {
      try {
        loading.value = true;
        const groupedItemsFiltered = groupedItems.value.filter((p) => p.qty);
        if (groupedItemsFiltered.length > 0) {
          // eslint-disable-next-line no-restricted-syntax
          for (const p of groupedItemsFiltered) {
            const productConfiguration = selectedProductsConfiguration.value
              // @ts-ignore
              .find(product => product.productUid === p.product.uid);
            // eslint-disable-next-line no-await-in-loop
            await addItem({ product: p.product, quantity: p.qty, productConfiguration });
            if (cartError.value.addItem) {
              sendNotification({
                id: Symbol('qty_update_error'),
                message: app.i18n.tc(addToCartError.value),
                type: 'danger',
                icon: 'info_shield',
                persist: false,
              });
              break;
            }
            p.qty = 0;
          }
        }
        loading.value = false;
      } catch {
        loading.value = false;
      }
    };

    watch(
      () => props.product,
      (productNewValue) => {
        const price = getGroupedProductPriceCommand(productNewValue);
        emit('update-price', price);
      },
      {
        deep: true,
      },
    );

    const { getMagentoImage } = useImage();
    const addToCartError = computed(() => cartError.value?.addItem?.message);

    const addItemToWishlist = async (product) => {
      if (isAuthenticated.value) {
        await addOrRemoveItem({ product });
      } else {
        await router.push(app.localeRoute({ name: 'customer-login-register', query: { redirect: route.value.path } }));
        sendNotification({
          id: Symbol('need_to_login'),
          message: app.i18n.tc('Need to login message'),
          type: 'warning',
          icon: 'check',
          persist: false,
        });
      }
    };

    const handleNotifyMe = (sku) => {
      emit('notify-me', sku);
    };

    return {
      addToCart,
      groupedItems,
      productPrice,
      loading,
      getMagentoImage,
      getProductName,
      getProductPrice,
      getProductPath,
      isInWishlist,
      isSomeGroupedItemsSelect,
      getProductThumbnailImage,
      getProductSmallImage,
      addToCartError,
      addItemToWishlist,
      isInCart,
      addItemToCart,
      getTagLine,
      selectedSize,
      handleSelectedSize,
      selectedProductsConfiguration,
      selectedProductsConfigurationReady,
      handleNotifyMe,
      ProductTypeEnum,
      differenceInLength,
      isSimpleProduct
    };
  },
});
